/**
 * Phase F (calendar animations): shared framer-motion props for an event chip so
 * the Day/Week/Month views animate consistently.
 *
 * - `layout` gives a same-view reschedule a FLIP position tween (Step 3). A chip
 *   keyed by a stable `eventKey` that changes top/left between renders animates to
 *   its new slot. Cross-container/recurring moves remount (key change) and fall
 *   back to no-FLIP — the destination AgentEffect highlight still fires.
 * - `exit` collapses opacity + height so a chip removed from the list (e.g. an
 *   agent delete) plays a brief exit while held by `<AnimatePresence>` (Step 2).
 *   Note: this animates a STILL-PRESENT-then-removed chip; it does not retain a
 *   snapshot of already-deleted data (see the view comments / report).
 *
 * happy-dom has no layout engine, so `layout`/FLIP is a no-op under test — the
 * chip still renders (asserted by the view tests), just without a measured tween.
 */
import { useEffect, useRef, useState } from 'react';
import type { MotionProps } from 'framer-motion';

/** FLIP + enter/exit for an absolutely-positioned timed chip. */
export const TIMED_CHIP_MOTION: MotionProps = {
  layout: 'position',
  initial: false,
  exit: { opacity: 0, scaleY: 0.6 },
  transition: { duration: 0.18, ease: 'easeOut' },
};

/** FLIP + enter/exit for an in-flow all-day / month chip (collapse its height). */
export const FLOW_CHIP_MOTION: MotionProps = {
  layout: true,
  initial: false,
  exit: { opacity: 0, height: 0, marginTop: 0, marginBottom: 0 },
  transition: { duration: 0.18, ease: 'easeOut' },
};

/**
 * Phase F (Step 4): focus recovery. When the currently-focused event chip (a node
 * carrying `data-event-id`) is removed from the present id set, move focus to a
 * fallback container so keyboard focus is never stranded on a vanished chip. Pass
 * the live set of present chip ids and a ref to the fallback element (the day/week
 * grid container). Best-effort: a no-op if focus isn't on a chip, the chip is
 * still present, or the fallback isn't mounted.
 */
export function useChipFocusRecovery(
  presentIds: Set<string>,
  fallbackRef: { current: HTMLElement | null },
): void {
  // Track the last-focused chip id via a document `focusin` listener — focusing a
  // chip does not trigger a React render, so an effect that only runs on render
  // would miss it. The ref always reflects the chip the user last focused.
  const lastFocusedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onFocusIn = (ev: FocusEvent) => {
      const chip = (ev.target as HTMLElement | null)?.closest?.('[data-event-id]') as
        | HTMLElement
        | null;
      lastFocusedIdRef.current = chip?.getAttribute('data-event-id') ?? null;
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  // On each list change, if the last-focused chip is gone AND focus has fallen to
  // the body (the browser blurs a removed node), recover to the fallback container.
  useEffect(() => {
    const prev = lastFocusedIdRef.current;
    if (!prev || presentIds.has(prev)) return;
    lastFocusedIdRef.current = null;

    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const stillOnAChip = active?.closest?.('[data-event-id]') != null;
    if (stillOnAChip) return; // user moved focus to another chip — leave it

    const fallback = fallbackRef.current;
    if (fallback) {
      if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
      fallback.focus({ preventScroll: true });
    }
  }, [presentIds, fallbackRef]);
}

/**
 * Exit-only motion for a month-grid chip. No `layout` FLIP: month chips are
 * wrapped by a dnd-kit `DraggableEvent` whose drag transform lives on the parent,
 * and a `layout` measurement on the inner node would fight that transform. A
 * removed chip still collapses out via `<AnimatePresence>`; a drag-reschedule
 * across day cells remounts (key change) and falls back to no-FLIP — the
 * destination AgentEffect highlight still fires.
 */
export const MONTH_CHIP_MOTION: MotionProps = {
  initial: false,
  exit: { opacity: 0, height: 0, marginTop: 0, marginBottom: 0 },
  transition: { duration: 0.18, ease: 'easeOut' },
};

/**
 * Keeps a conditionally-rendered container (e.g. DayView's `{allDayEvents.length
 * > 0 && …}` all-day band) mounted for a brief grace window AFTER its last item
 * disappears, so an `<AnimatePresence>` child inside it can finish its exit
 * animation before the wrapper unmounts and clips it. Returns true while the
 * band should stay rendered.
 */
export function useKeepMountedWhileExiting(hasItems: boolean, graceMs = 260): boolean {
  const [keep, setKeep] = useState(hasItems);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hasItems) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setKeep(true);
      return;
    }
    // No items now — hold the wrapper open for the grace window, then drop it.
    timerRef.current = setTimeout(() => setKeep(false), graceMs);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [hasItems, graceMs]);

  return hasItems || keep;
}
